/**
 * Symbol data cache — survives React hot-reloads via window.__SC__.
 * Module-level Maps reset on every HMR save; window persists until page refresh.
 * TTL: 5 minutes — covers normal intraday re-visits.
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TTL = 5 * 60_000; // 5 min

// Survive hot-module-replacement by anchoring to window
if (!window.__SC__)       window.__SC__  = new Map();
if (!window.__SC_FLY__)   window.__SC_FLY__ = new Map();
const store  = window.__SC__;     // symbol → { data, ts }
const flying = window.__SC_FLY__; // symbol → Promise

const settle = p => p.then(r => r.data).catch(() => null);

async function fetchAll(symbol) {
  const [bundle, ohlcv, intraday, iv, regime, expirations, earnings] = await Promise.all([
    settle(axios.get(`${API}/market/symbol-bundle?symbol=${symbol}&include_news=true&news_limit=10`)),
    settle(axios.get(`${API}/market/ohlcv?symbol=${symbol}&days=90`)),
    settle(axios.get(`${API}/market/intraday?symbol=${symbol}&interval=5m&range_val=1d`)),
    settle(axios.get(`${API}/iv/history/full?symbol=${symbol}&days=30`)),
    settle(axios.get(`${API}/zi/regime?symbol=${symbol}`)),
    settle(axios.get(`${API}/market/expirations?symbol=${symbol}`)),
    settle(axios.get(`${API}/earnings?symbols=${symbol}`)),
  ]);
  return { bundle, ohlcv, intraday, iv, regime, expirations, earnings };
}

/** Return cached data if still fresh, otherwise null. */
export function getCached(symbol) {
  const entry = store.get(symbol.toUpperCase());
  if (!entry) return null;
  if (Date.now() - entry.ts > TTL) { store.delete(symbol.toUpperCase()); return null; }
  return entry.data;
}

/** Prefetch a symbol in background. Deduplicates concurrent calls. */
export async function prefetch(symbol) {
  const sym = symbol.toUpperCase();
  const cached = getCached(sym);
  if (cached) return cached;
  if (flying.has(sym)) return flying.get(sym);

  const p = fetchAll(sym)
    .then(data => {
      store.set(sym, { data, ts: Date.now() });
      flying.delete(sym);
      return data;
    })
    .catch(e => { flying.delete(sym); throw e; });

  flying.set(sym, p);
  return p;
}

/** Called by TickerCard after its own fetch — populates cache for next open. */
export function hydrate(symbol, data) {
  store.set(symbol.toUpperCase(), { data, ts: Date.now() });
}

/** True if actively being fetched (so TickerCard can skip duplicate fetch). */
export function isPrefetching(symbol) {
  return flying.has(symbol.toUpperCase());
}

/** Get the in-flight promise so TickerCard can await it instead of re-fetching. */
export function getInflight(symbol) {
  return flying.get(symbol.toUpperCase()) || null;
}
